import React, { useState, useMemo } from "react";
import {
  useQualificationsQuery,
  useDeleteQualificationMutation,
} from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { Trans } from "react-i18next";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
  Edit02Icon,
  GraduationScrollFreeIcons,
  AddSquareIcon,
} from "@hugeicons/core-free-icons";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { useClientSearch } from "@/utils/hooks/useClientSearch";

// Empty state component for when no qualifications are found
const EmptyState = ({ hasSearch }) => {
  const { t } = useTranslation();

  return (
    <div className="empty-state text-center py-5">
      <div className="empty-state-icon mb-3">
        <HugeiconsIcon
          icon={GraduationScrollFreeIcons}
          size={48}
          className="text-muted"
        />
      </div>
      <h4 className="empty-state-title mb-2">{t("NO_QUALIFICATIONS_FOUND")}</h4>
      <p className="empty-state-description text-muted mb-4">
        {t("NO_QUALIFICATIONS_DESCRIPTION")}
      </p>
      {!hasSearch && (
        <Link to="create" className="btn btn-primary">
          <HugeiconsIcon
            icon={GraduationScrollFreeIcons}
            size={16}
            className="me-2"
          />
          {t("CREATE_FIRST_QUALIFICATION")}
        </Link>
      )}
    </div>
  );
};

export const Qualifications = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    qualificationId: null,
  });
  const { data, isLoading, isFetching } = useQualificationsQuery({
    page: currentPage,
    per_page: 10,
  });
  const qualifications = data?.data?.data ?? [];
  const { filteredData, ...restClientSearch } = useClientSearch(qualifications);

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const loading = isLoading || isFetching;

  // Delete mutation
  const { mutate: deleteQualification, isPending: isDeleting } =
    useDeleteQualificationMutation();

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle delete modal
  const handleDeleteClick = (qualificationId) => {
    setDeleteModalState({ isOpen: true, qualificationId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, qualificationId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.qualificationId) {
      deleteQualification(deleteModalState.qualificationId, {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, qualificationId: null });
          queryClient.invalidateQueries({ queryKey: ["qualifications"] });
        },
      });
    }
  };

  // Define columns configuration
  const columns = useMemo(
    () => [
      {
        name: t("NAME_AR"),
        key: "name_ar",
        filterable: true,
      },
      {
        name: t("NAME_EN"),
        key: "name_en",
        filterable: true,
      },
      { name: t("ACTIONS"), key: "actions", filterable: false },
    ],
    [t]
  );

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`/app/seasonal_hr/basic-data/qualifications/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  // Create button component
  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={loading}
    >
      {t("CREATE_QUALIFICATION")}
    </BtnAsLink>
  );

  return (
    <Container fluid>
      <TableC
        data={filteredData}
        {...restClientSearch}
        columns={columns}
        loading={loading} undefined
        
        actions={getActions}
        renderActions={createButton}
        emptyState={EmptyState}
        minVisibleColumns={2}
        skeletonCount={3}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_QUALIFICATION"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </Container>
  );
};
